/**
 * El resumen del análisis de IA llega como un texto corrido, sin saltos de línea,
 * con toda la estructura adentro: una introducción, los hallazgos numerados —cada
 * uno con "Dónde", "Qué pasó" y "Qué hacer"— y una frase de cierre.
 *
 * Leído así es una pared de texto. Acá se reconoce esa estructura para poder
 * mostrarla como lo que es. Si el texto no tiene la forma esperada no se fuerza
 * nada: se marca como no estructurado y la pantalla lo muestra tal cual vino.
 */

export interface SummaryFinding {
    /** Número con el que aparece en el texto. */
    index: number;
    title: string;
    where?: string;
    what?: string;
    action?: string;
}

export interface ParsedSummary {
    intro?: string;
    findings: SummaryFinding[];
    /** Frase final, del tipo "los otros 9 pacientes no presentaron problemas". */
    outro?: string;
    /** Se reconoció la estructura de hallazgos. Si no, hay que mostrar el texto crudo. */
    isStructured: boolean;
    /** El texto original, ya limpio de espacios repetidos. */
    raw: string;
}

/** Las etiquetas del texto, tolerando que vengan sin tilde. */
const WHERE_LABEL = /D[oó]nde\s*:/i;
const WHAT_LABEL = /Qu[eé]\s+pas[oó]\s*:/i;
const ACTION_LABEL = /Qu[eé]\s+hacer\s*:/i;

/** Un hallazgo arranca con "N." seguido de su título en mayúscula. */
const FINDING_START = /(?:^|\s)(\d{1,2})\.\s+(?=[A-ZÁÉÍÓÚÑ])/g;

/**
 * Cierre del resumen: una frase sobre el resto de los pacientes. Es una heurística
 * declarada, no una regla del formato: si no aparece, no se inventa un cierre.
 */
const OUTRO = /((?:Los|Las)\s+(?:otros|otras|restantes)[^.]*\.|El\s+resto\s+de\s+los\s+pacientes[^.]*\.)\s*$/i;

const clean = (value: string): string => value.replace(/\s+/g, ' ').trim();

const cut = (text: string, label: RegExp): { before: string; after?: string } => {
    const match = label.exec(text);
    if (!match || match.index === undefined) return { before: text };
    return {
        before: text.slice(0, match.index),
        after: text.slice(match.index + match[0].length)
    };
};

interface FindingPosition {
    index: number;
    start: number;
    titleStart: number;
}

/**
 * Hasta dónde se busca el "Dónde:" que confirma que ahí empieza un hallazgo.
 * Alcanza para un título; más que eso ya sería el cuerpo del hallazgo anterior.
 */
const WHERE_LOOKAHEAD = 120;

/**
 * Dónde empieza cada hallazgo.
 *
 * Un número seguido de punto no alcanza para decidirlo: el texto está lleno de
 * frases que terminan así —"Visita 3.", "el branch vigente del paciente es 33."— y
 * tomarlas como títulos parte los hallazgos por la mitad. La señal firme es que un
 * hallazgo de verdad trae su "Dónde:" enseguida del título; encima tienen que
 * formar la serie 1, 2, 3…
 */
const findingPositions = (text: string): FindingPosition[] => {
    const candidates: FindingPosition[] = [];
    FINDING_START.lastIndex = 0;

    let match = FINDING_START.exec(text);
    while (match !== null) {
        candidates.push({
            index: Number(match[1]),
            start: match.index,
            titleStart: match.index + match[0].length
        });
        match = FINDING_START.exec(text);
    }

    const sequential: FindingPosition[] = [];
    let expected = 1;

    candidates.forEach((candidate) => {
        const ahead = text.slice(candidate.titleStart, candidate.titleStart + WHERE_LOOKAHEAD);
        if (!WHERE_LABEL.test(ahead)) return;
        if (candidate.index !== expected) return;
        sequential.push(candidate);
        expected += 1;
    });

    return sequential;
};

export const parseAnalysisSummary = (summary?: string | null): ParsedSummary => {
    const raw = clean(summary ?? '');
    if (!raw) return { findings: [], isStructured: false, raw: '' };

    const positions = findingPositions(raw);
    if (positions.length === 0) return { findings: [], isStructured: false, raw };

    const intro = clean(raw.slice(0, positions[0].start));

    const findings: SummaryFinding[] = positions.map((position, order) => {
        const end = order + 1 < positions.length ? positions[order + 1].start : raw.length;
        const block = raw.slice(position.titleStart, end);

        const byWhere = cut(block, WHERE_LABEL);
        const byWhat = cut(byWhere.after ?? '', WHAT_LABEL);
        const byAction = cut(byWhat.after ?? '', ACTION_LABEL);

        return {
            index: position.index,
            title: clean(byWhere.before) || `Hallazgo ${position.index}`,
            where: clean(byWhat.before) || undefined,
            what: clean(byAction.before) || undefined,
            action: clean(byAction.after ?? '') || undefined
        };
    });

    // El cierre queda pegado al final del último "Qué hacer": se separa si aparece.
    const last = findings[findings.length - 1];
    let outro: string | undefined;
    if (last?.action) {
        const match = OUTRO.exec(last.action);
        if (match) {
            outro = clean(match[1]);
            last.action = clean(last.action.slice(0, match.index)) || undefined;
        }
    }

    return { intro: intro || undefined, findings, outro, isStructured: true, raw };
};
